"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { ThemeColors, ThemeService } from "@/services/themeService";

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (theme: "light" | "dark") => void;
  themeColors: ThemeColors | null;
  setThemeColors: (colors: ThemeColors) => void;
  loadTheme: () => Promise<void>;
  isThemeLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [isDark, setIsDark] = useState(false);
  const [themeColors, setThemeColors] = useState<ThemeColors | null>(null);
  const [isThemeLoading, setIsThemeLoading] = useState(true);

  const loadTheme = useCallback(async () => {
    setIsThemeLoading(true);
    try {
      const theme = await ThemeService.getTheme();
      setThemeColors(theme);
    } catch (error) {
      console.error("Failed to load theme colors:", error);
      setThemeColors(null);
    } finally {
      setIsThemeLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if dark mode is enabled in localStorage
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const initialTheme = savedTheme === "dark" || (!savedTheme && prefersDark);

    setIsDark(initialTheme);
    document.documentElement.classList.toggle("dark", initialTheme);

    // Load theme colors
    loadTheme();
  }, [loadTheme]);

  const setTheme = (theme: "light" | "dark") => {
    const newTheme = theme === "dark";
    setIsDark(newTheme);
    document.documentElement.classList.toggle("dark", newTheme);
    localStorage.setItem("theme", theme);
  };

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.classList.toggle("dark", newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider
      value={{
        isDark,
        toggleTheme,
        setTheme,
        themeColors,
        setThemeColors,
        loadTheme,
        isThemeLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
